import { useMemo, useState } from 'react';

import {
  ColumnDef,
  SortingState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { toast } from 'sonner';

import { Role } from '@/api/queries/useGetRoles';
import { PendingInvite } from '@/api/types/backend-dto-types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { ScrollableTable } from '@/new/components/table/scrollable-table';

interface PendingMatterInvitesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invites: PendingInvite[];
  roles: Role[];
  canManageRoles: boolean;
  roleEditingDisabledReason?: string;
  canRemoveInvites: boolean;
  onRoleChange: (inviteId: string, roleId: string | null) => Promise<unknown>;
  onRemove: (inviteId: string) => void;
}

export function PendingMatterInvitesDialog({
  open,
  onOpenChange,
  invites,
  roles,
  canManageRoles,
  roleEditingDisabledReason,
  canRemoveInvites,
  onRoleChange,
  onRemove,
}: PendingMatterInvitesDialogProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const columns = useMemo<ColumnDef<PendingInvite>[]>(() => {
    const result: ColumnDef<PendingInvite>[] = [
      {
        accessorKey: 'userEmail',
        header: 'Email',
        cell: ({ row }) => <span className="lowercase">{row.original.userEmail}</span>,
      },
      {
        accessorKey: 'createdDate',
        header: 'Invited',
        cell: ({ row }) => (
          <span className="text-gray-500">{new Date(row.original.createdDate).toLocaleDateString()}</span>
        ),
      },
      {
        accessorKey: 'roleName',
        header: 'Matter role',
        cell: ({ row }) => {
          const invite = row.original;
          return canManageRoles ? (
            <Select
              value={invite.roleId ?? 'none'}
              onValueChange={(value) =>
                onRoleChange(invite.id, value === 'none' ? null : value).catch(() =>
                  toast.error('Failed to update the pending user role'),
                )
              }
            >
              <SelectTrigger className="h-8 w-44">
                <SelectValue placeholder="Use global role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Use global role</SelectItem>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : roleEditingDisabledReason ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <span className="inline-flex cursor-help items-center gap-1.5 text-gray-500" tabIndex={0}>
                  {invite.roleName ?? 'Global role'}
                  <Icon className="lucide--info h-3.5 w-3.5 text-gray-400" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-72">
                {roleEditingDisabledReason}
              </TooltipContent>
            </Tooltip>
          ) : (
            <span className="text-gray-500">{invite.roleName ?? 'Global role'}</span>
          );
        },
      },
    ];

    if (canRemoveInvites) {
      result.push({
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-right">
            <Button variant="ghost" onClick={() => onRemove(row.original.id)}>
              Revoke Invite
              <Icon className="lucide--x" />
            </Button>
          </div>
        ),
      });
    }

    return result;
  }, [canManageRoles, canRemoveInvites, onRemove, onRoleChange, roleEditingDisabledReason, roles]);

  const table = useReactTable({
    data: invites,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getRowId: (row) => row.id,
    globalFilterFn: 'includesString',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] max-w-3xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Pending Matter invites</DialogTitle>
          <p className="text-sm text-gray-500">
            Invited users will be added to this Matter when they create their account within 30 days.
          </p>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-hidden pt-1">
          <ScrollableTable
            table={table}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            plural="pending invites"
            className="h-full max-h-[55vh] p-0 px-1"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
